import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Text,
  VStack,
  HStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  Box,
} from '@chakra-ui/react';
import { FaCoins } from 'react-icons/fa';
import { giftMemberUnits } from '../../Redux/Login/action';

const GiftCreditsModal = ({ isOpen, onClose, recipientId }) => {
  const [amount, setAmount] = useState(1);
  const [step, setStep] = useState(1);
  const dispatch = useDispatch();
  const toast = useToast();
  
  // Get user data from Redux state
  const userData = useSelector(state => state.auth?.profile);
  const loading = useSelector(state => state.auth?.giftMU?.loading);
  const availableMU = userData?.accountCredits || 0;
  
  // Function to handle amount change with validation
  const handleAmountChange = (value) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue > 0) {
      setAmount(Math.min(numValue, availableMU));
    }
  };
  
  // Function to handle gift submission
  const handleSubmit = async () => {
    if (step < 3) {
      setStep(step + 1);
      return;
    }
    
    const result = await dispatch(giftMemberUnits(recipientId, amount));
    
    if (result.success) {
      toast({
        title: "MU Gifted Successfully",
        description: `You sent ${amount} MU to ${recipientId}`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setStep(1);
      setAmount(1);
      onClose();
    } else {
      toast({
        title: "Failed to Gift MU",
        description: result.error || "An error occurred while gifting Member Units",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };
  
  // Function to handle cancel and reset
  const handleCancel = () => {
    setStep(1);
    setAmount(1);
    onClose();
  };
  
  return (
    <Modal isOpen={isOpen} onClose={handleCancel} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="gray.800" color="white" mx={4}>
        <ModalHeader pb={2}>
          <HStack>
            <FaCoins color="#F6E05E" />
            <Text fontSize="lg">Gift MU to {recipientId}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          {step === 1 && (
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between" bg="whiteAlpha.100" p={3} borderRadius="md">
                <Text fontSize="sm" color="gray.400">Your balance</Text>
                <HStack>
                  <FaCoins color="#F6E05E" size="12" />
                  <Text fontWeight="bold" color="#F6E05E">{availableMU.toLocaleString()} MU</Text>
                </HStack>
              </HStack>
              <FormControl>
                <FormLabel fontSize="sm" color="gray.300">Amount to Gift</FormLabel>
                <NumberInput 
                  min={1} 
                  max={availableMU} 
                  value={amount} 
                  onChange={handleAmountChange}
                  size="lg"
                >
                  <NumberInputField 
                    bg="whiteAlpha.100" 
                    border="1px solid" 
                    borderColor="whiteAlpha.200"
                    _hover={{ borderColor: 'whiteAlpha.300' }}
                    _focus={{ borderColor: '#F6E05E', boxShadow: '0 0 0 1px #F6E05E' }}
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper color="gray.400" borderColor="whiteAlpha.200" />
                    <NumberDecrementStepper color="gray.400" borderColor="whiteAlpha.200" />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </VStack>
          )}
          
          {step === 2 && (
            <Box bg="whiteAlpha.100" border="1px solid" borderColor="yellow.600" borderRadius="md" p={4}>
              <HStack mb={2}>
                <Text fontSize="lg">⚠️</Text>
                <Text fontWeight="bold" color="yellow.300" fontSize="sm">Please confirm your gift</Text>
              </HStack>
              <Text fontSize="sm" color="gray.200">
                You are about to send <Text as="span" fontWeight="bold" color="#F6E05E">{amount} MU</Text> to <Text as="span" fontWeight="bold" color="white">{recipientId}</Text>.
              </Text>
              <Text mt={2} fontSize="xs" color="gray.400">
                This transaction cannot be reversed once completed.
              </Text>
            </Box>
          )}
          
          {step === 3 && (
            <Box bg="whiteAlpha.100" border="1px solid" borderColor="red.500" borderRadius="md" p={4}>
              <HStack mb={2}>
                <Text fontSize="lg">🛑</Text>
                <Text fontWeight="bold" color="red.300" fontSize="sm">Final confirmation required</Text>
              </HStack>
              <Text fontSize="sm" color="gray.200">
                You are sending <Text as="span" fontWeight="bold" color="#F6E05E">{amount} MU</Text> to <Text as="span" fontWeight="bold" color="white">{recipientId}</Text>.
              </Text>
              <Text mt={2} fontSize="sm" fontWeight="bold" color="red.300">
                This is your last chance to cancel.
              </Text>
              <Text mt={1} fontSize="xs" color="gray.400">
                Member Units cannot be refunded once sent.
              </Text>
            </Box>
          )}
        </ModalBody>
        
        <ModalFooter pt={2}>
          <Button 
            size="sm" 
            variant="ghost" 
            color="gray.400" 
            mr={3} 
            onClick={handleCancel}
            _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
          >
            Cancel
          </Button>
          <Button 
            size="sm"
            bg={step === 3 ? "red.600" : step === 2 ? "yellow.600" : "#F6E05E"}
            color={step === 1 ? "gray.900" : "white"}
            _hover={{ bg: step === 3 ? "red.500" : step === 2 ? "yellow.500" : "yellow.300" }}
            onClick={handleSubmit}
            isLoading={loading}
            isDisabled={amount <= 0 || amount > availableMU}
          >
            {step === 1 ? "Continue" : step === 2 ? "Confirm Gift" : "Send MU"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default GiftCreditsModal;
